/**
 * Realtime client for job and discovery progress (OA-56).
 *
 * One WebSocket per tab to the API's `/api/ws`, shared by every component that
 * watches a job or discovery job. Components never touch it directly; they use
 * `useJobEvents` / `useDiscoveryEvents` (src/hooks/useRealtimeEvents.ts).
 *
 * Contract with the API (officea11y-api docs/REALTIME.md):
 *
 * - The browser authenticates with the HttpOnly session cookie it already
 *   holds; nothing here handles a credential.
 * - Events are hints, not a log. Nothing is replayed after a disconnect, so on
 *   every `hello` (first connect and each reconnect) the client re-subscribes
 *   and tells each watcher to resync, i.e. fetch current state once over REST.
 * - While the socket is not open, watchers keep polling the existing REST
 *   endpoints; `isConnected()` is how they choose the interval.
 * - Close code 4401 means the session is no longer valid: stop reconnecting.
 *   The next REST call gets the 401 and the app's normal handling takes over.
 *   The next page that starts watching (after signing in again, say) tries
 *   once more, so a sign-in without a reload gets live updates back.
 *
 * The socket is opened lazily on the first subscription and closed a short
 * while after the last one goes away, so pages that watch nothing hold no
 * connection.
 */

import { setNotificationTransport } from './notifications';

export type RealtimeTopic = 'job' | 'discovery';

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'unavailable';

export interface JobStatusEvent {
  type: 'job.status';
  id: string;
  ts: string;
  data: {
    jobId: string;
    status: string;
    retryCount: number | null;
    maxRetries: number | null;
  };
}

export interface JobProgressEvent {
  type: 'job.progress';
  id: string;
  ts: string;
  data: { jobId: string; status: 'processing'; stage: string };
}

export interface DiscoveryEventData {
  discoveryJobId: string;
  status: string;
  pagesScanned: number;
  pdfsFound: number;
  errorCount: number;
  lastActivityAt: string | null;
  completionPercentage?: number | null;
}

export interface DiscoveryStatusEvent {
  type: 'discovery.status';
  id: string;
  ts: string;
  data: DiscoveryEventData;
}

export interface DiscoveryProgressEvent {
  type: 'discovery.progress';
  id: string;
  ts: string;
  data: DiscoveryEventData;
}

export type RealtimeEvent =
  | JobStatusEvent
  | JobProgressEvent
  | DiscoveryStatusEvent
  | DiscoveryProgressEvent;

export interface RealtimeWatcher {
  /** A job or discovery event for the watched resource. */
  onEvent: (event: RealtimeEvent) => void;
  /** The socket (re)connected: fetch current state once. */
  onResync?: () => void;
}

/** Close code the API uses when the session is no longer valid. */
export const CLOSE_UNAUTHORIZED = 4401;

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const IDLE_CLOSE_MS = 15_000;

/**
 * The WebSocket URL for an API base URL.
 *
 * @param {string} apiBase - REACT_APP_API_URL, e.g. `http://localhost:3002/api` or `/api`
 * @param {Pick<Location, 'protocol' | 'host'>} location - The page location, for relative bases
 * @returns {string} `ws(s)://host/api/ws`
 */
export function buildRealtimeUrl(
  apiBase: string,
  location: Pick<Location, 'protocol' | 'host'>
): string {
  const base = new URL(apiBase, `${location.protocol}//${location.host}`);
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = base.pathname.replace(/\/$/, '');
  return `${protocol}//${base.host}${path}/ws`;
}

type SocketFactory = (url: string) => WebSocket;

interface RealtimeClientOptions {
  /** Never open a socket; every watcher keeps polling. */
  disabled?: boolean;
  url?: string;
  createSocket?: SocketFactory;
  random?: () => number;
}

/**
 * Shared, reference-counted WebSocket client.
 */
export class RealtimeClient {
  private socket: WebSocket | null = null;
  private status: RealtimeStatus = 'idle';
  private readonly watchers = new Map<string, Set<RealtimeWatcher>>();
  // Raw `notification` frames go to these (OA-220, src/services/notifications.ts).
  private readonly notificationListeners = new Set<(frame: unknown) => void>();
  private readonly statusListeners = new Set<(status: RealtimeStatus) => void>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  // Set by a 4401 close. Unlike `stopped` it is cleared by the next watch().
  private unauthorized = false;
  private readonly url: string | null;
  private readonly createSocket: SocketFactory | null;
  private readonly random: () => number;

  constructor(options: RealtimeClientOptions = {}) {
    const hasWebSocket = typeof WebSocket !== 'undefined';
    const fallback: SocketFactory | null = hasWebSocket ? url => new WebSocket(url) : null;
    this.createSocket = options.disabled ? null : (options.createSocket ?? fallback);
    this.random = options.random ?? Math.random;
    this.url = options.url ?? null;
    if (!this.createSocket) {
      this.status = 'unavailable';
    }
  }

  /** Whether events are currently flowing. */
  isConnected(): boolean {
    return this.status === 'open';
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  /**
   * Listen for connection status changes.
   *
   * @param {(status: RealtimeStatus) => void} listener - Called with each new status
   * @returns {() => void} Unsubscribe function
   */
  onStatusChange(listener: (status: RealtimeStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  /**
   * Watch a job or discovery job.
   *
   * @param {RealtimeTopic} topic - `job` or `discovery`
   * @param {string} id - Resource id
   * @param {RealtimeWatcher} watcher - Event and resync callbacks
   * @returns {() => void} Function that stops watching
   */
  watch(topic: RealtimeTopic, id: string, watcher: RealtimeWatcher): () => void {
    const key = `${topic}:${id}`;
    let set = this.watchers.get(key);
    const isNewKey = !set;
    if (!set) {
      set = new Set();
      this.watchers.set(key, set);
    }
    set.add(watcher);
    this.clearIdleTimer();
    // A page that starts watching after a 4401 gets one fresh attempt: the
    // user may have signed in again without a reload. If the session is still
    // gone it is one more 4401, not a retry loop.
    if (isNewKey) {
      this.unauthorized = false;
    }

    if (this.status === 'open' && isNewKey) {
      this.sendMessage({ type: 'subscribe', topic, id });
    } else if (this.status === 'idle' || this.status === 'closed') {
      this.connect();
    }

    return () => {
      const current = this.watchers.get(key);
      if (!current) {
        return;
      }
      current.delete(watcher);
      if (current.size === 0) {
        this.watchers.delete(key);
        if (this.status === 'open') {
          this.sendMessage({ type: 'unsubscribe', topic, id });
        }
      }
      if (!this.hasListeners()) {
        this.scheduleIdleClose();
      }
    };
  }

  /**
   * Receive the owner-only `notification` frames (job or discovery finished),
   * which need no subscription. The socket stays open while anyone listens,
   * as it does while anything is watched.
   *
   * @param {(frame: unknown) => void} onFrame - Called with each raw frame
   * @returns {() => void} Function that stops listening
   */
  listenForNotifications(onFrame: (frame: unknown) => void): () => void {
    this.notificationListeners.add(onFrame);
    this.clearIdleTimer();
    this.unauthorized = false;
    if (this.status === 'idle' || this.status === 'closed') {
      this.connect();
    }
    return () => {
      this.notificationListeners.delete(onFrame);
      if (!this.hasListeners()) {
        this.scheduleIdleClose();
      }
    };
  }

  /**
   * Close the socket and stop reconnecting for good. Used by tests; sign-out
   * does not need it, since the pages that watch unmount and the idle close
   * follows.
   */
  shutdown(): void {
    this.stopped = true;
    this.clearReconnectTimer();
    this.clearIdleTimer();
    this.socket?.close(1000);
    this.socket = null;
    this.setStatus('closed');
  }

  private connect(): void {
    if (!this.createSocket || this.stopped || this.unauthorized || this.socket) {
      return;
    }
    this.clearReconnectTimer();
    this.setStatus('connecting');

    let socket: WebSocket;
    try {
      socket = this.createSocket(this.url ?? defaultUrl());
    } catch {
      this.setStatus('closed');
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.addEventListener('message', message => this.handleMessage(message));
    socket.addEventListener('close', closeEvent => this.handleClose(socket, closeEvent.code));
    // An error is always followed by close; nothing to do here.
    socket.addEventListener('error', () => {});
  }

  private handleMessage(message: MessageEvent): void {
    type Frame = { type?: string; id?: string } & Record<string, unknown>;
    let frame: Frame;
    try {
      frame = JSON.parse(String(message.data)) as Frame;
    } catch {
      return;
    }
    if (!frame || typeof frame.type !== 'string') {
      return;
    }

    if (frame.type === 'notification') {
      this.deliverNotification(frame);
      return;
    }

    if (frame.type === 'hello') {
      this.reconnectAttempts = 0;
      this.setStatus('open');
      for (const key of this.watchers.keys()) {
        const [topic, id] = splitKey(key);
        this.sendMessage({ type: 'subscribe', topic, id });
      }
      // Anything may have changed while disconnected: every watcher fetches once.
      for (const set of this.watchers.values()) {
        for (const watcher of set) {
          watcher.onResync?.();
        }
      }
      return;
    }

    const topic = topicOf(frame.type);
    if (!topic || typeof frame.id !== 'string') {
      return;
    }
    const set = this.watchers.get(`${topic}:${frame.id}`);
    if (!set) {
      return;
    }
    for (const watcher of set) {
      watcher.onEvent(frame as unknown as RealtimeEvent);
    }
  }

  private handleClose(socket: WebSocket, code: number): void {
    if (this.socket !== socket) {
      return;
    }
    this.socket = null;
    this.setStatus('closed');
    if (code === CLOSE_UNAUTHORIZED) {
      // The session ended. Polling continues; the next REST call gets the 401.
      this.unauthorized = true;
      this.clearReconnectTimer();
      return;
    }
    if (this.hasListeners()) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.unauthorized || this.reconnectTimer) {
      return;
    }
    const exponential = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.reconnectAttempts);
    // Full jitter, so a server restart is not met by every tab at once.
    const delay = Math.round(exponential / 2 + (this.random() * exponential) / 2);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private deliverNotification(frame: unknown): void {
    for (const listener of this.notificationListeners) {
      listener(frame);
    }
  }

  private hasListeners(): boolean {
    return this.watchers.size > 0 || this.notificationListeners.size > 0;
  }

  private scheduleIdleClose(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (!this.hasListeners()) {
        this.clearReconnectTimer();
        const socket = this.socket;
        this.socket = null;
        socket?.close(1000);
        this.setStatus('idle');
      }
    }, IDLE_CLOSE_MS);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private sendMessage(message: Record<string, unknown>): void {
    if (this.socket && this.socket.readyState === 1) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) {
      return;
    }
    this.status = status;
    for (const listener of this.statusListeners) {
      listener(status);
    }
  }
}

function splitKey(key: string): [RealtimeTopic, string] {
  const index = key.indexOf(':');
  return [key.slice(0, index) as RealtimeTopic, key.slice(index + 1)];
}

function topicOf(type: string): RealtimeTopic | null {
  if (type.startsWith('job.')) {
    return 'job';
  }
  if (type.startsWith('discovery.')) {
    return 'discovery';
  }
  return null;
}

function defaultUrl(): string {
  return buildRealtimeUrl(
    process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
    globalThis.location
  );
}

/**
 * The app-wide client. Disabled when REACT_APP_REALTIME is `false`, in which
 * case every watcher simply keeps polling, and under Jest, where jsdom would
 * otherwise dial a server that is not there (tests inject their own client).
 */
export const realtimeClient = new RealtimeClient({
  disabled: process.env.REACT_APP_REALTIME === 'false' || process.env.NODE_ENV === 'test',
});

// The app-wide notification region (OA-220) receives its frames through this
// client. Not registered when realtime is off: the region then renders nothing.
if (realtimeClient.getStatus() !== 'unavailable') {
  setNotificationTransport({
    listen: onFrame => realtimeClient.listenForNotifications(onFrame),
  });
}
